import { parseJpegDimensions } from './jpeg';

/** Lightweight PDF 1.4 writer for A4 portrait Clinio reports (Helvetica + Helvetica-Bold). */

export const PAGE_WIDTH = 595.28;
export const PAGE_HEIGHT = 841.89;
export const MARGIN = 36;
export const FOOTER_H = 28;
export const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;

export const COLORS = {
  page: [0.973, 0.973, 0.969] as const,
  paper: [1, 1, 1] as const,
  text: [0.059, 0.09, 0.165] as const,
  muted: [0.392, 0.455, 0.545] as const,
  line: [0.886, 0.898, 0.91] as const,
  headerBg: [0.953, 0.941, 0.98] as const,
  figureBg: [1, 1, 1] as const,
  altRow: [0.976, 0.976, 0.973] as const,
  totalBg: [0.953, 0.941, 0.98] as const,
  accent: [0.388, 0.4, 0.851] as const,
  accentSoft: [0.91, 0.914, 0.98] as const,
  mint: [0.933, 0.969, 0.953] as const,
  mintAccent: [0.239, 0.478, 0.408] as const,
  sky: [0.933, 0.957, 0.984] as const,
  skyAccent: [0.231, 0.435, 0.576] as const,
  lavender: [0.953, 0.941, 0.98] as const,
  lavenderAccent: [0.42, 0.357, 0.549] as const,
  peach: [0.984, 0.953, 0.925] as const,
  peachAccent: [0.659, 0.416, 0.271] as const,
  bar: [0.675, 0.682, 0.898] as const,
  barTrack: [0.918, 0.922, 0.937] as const,
  white: [1, 1, 1] as const,
};

export const CARD_TINTS = [
  { bg: COLORS.mint, accent: COLORS.mintAccent },
  { bg: COLORS.sky, accent: COLORS.skyAccent },
  { bg: COLORS.lavender, accent: COLORS.lavenderAccent },
  { bg: COLORS.peach, accent: COLORS.peachAccent },
  { bg: COLORS.accentSoft, accent: COLORS.accent },
] as const;

export type Rgb = readonly [number, number, number];

const HELVETICA_WIDTHS: Record<number, number> = {
  32: 278,
  33: 278,
  34: 355,
  35: 556,
  36: 556,
  37: 889,
  38: 667,
  39: 191,
  40: 333,
  41: 333,
  42: 389,
  43: 584,
  44: 278,
  45: 333,
  46: 278,
  47: 278,
  48: 556,
  49: 556,
  50: 556,
  51: 556,
  52: 556,
  53: 556,
  54: 556,
  55: 556,
  56: 556,
  57: 556,
  58: 278,
  59: 278,
  60: 584,
  61: 584,
  62: 584,
  63: 556,
  64: 1015,
  65: 667,
  66: 667,
  67: 722,
  68: 722,
  69: 667,
  70: 611,
  71: 778,
  72: 722,
  73: 278,
  74: 500,
  75: 667,
  76: 556,
  77: 833,
  78: 722,
  79: 778,
  80: 667,
  81: 778,
  82: 722,
  83: 667,
  84: 611,
  85: 722,
  86: 667,
  87: 944,
  88: 667,
  89: 667,
  90: 611,
  91: 278,
  92: 278,
  93: 278,
  94: 469,
  95: 556,
  96: 333,
  97: 556,
  98: 556,
  99: 500,
  100: 556,
  101: 556,
  102: 278,
  103: 556,
  104: 556,
  105: 222,
  106: 222,
  107: 500,
  108: 222,
  109: 833,
  110: 556,
  111: 556,
  112: 556,
  113: 556,
  114: 333,
  115: 500,
  116: 278,
  117: 556,
  118: 500,
  119: 722,
  120: 500,
  121: 500,
  122: 500,
  123: 334,
  124: 260,
  125: 334,
  126: 584,
  196: 667,
  197: 667,
  198: 1000,
  201: 667,
  214: 778,
  216: 778,
  220: 722,
  223: 611,
  228: 556,
  229: 556,
  230: 889,
  233: 556,
  246: 556,
  248: 611,
  252: 556,
};

const WIN_ANSI: Record<number, number> = {
  0x00c4: 196,
  0x00c5: 197,
  0x00c6: 198,
  0x00c9: 201,
  0x00d6: 214,
  0x00d8: 216,
  0x00dc: 220,
  0x00df: 223,
  0x00e4: 228,
  0x00e5: 229,
  0x00e6: 230,
  0x00e9: 233,
  0x00f6: 246,
  0x00f8: 248,
  0x00fc: 252,
  0x2013: 150,
  0x2014: 151,
  0x2018: 145,
  0x2019: 146,
  0x201c: 147,
  0x201d: 148,
  0x2026: 133,
  0x00a0: 32,
  0x202f: 32,
  0x2007: 32,
  0x2009: 32,
  0x200a: 32,
  0x00ad: 45,
  0x00b7: 183,
};

function toWinAnsiCode(char: string): number {
  const code = char.charCodeAt(0);
  if (code <= 127) return code;
  return WIN_ANSI[code] ?? 63;
}

export function pdfSafeText(text: string): string {
  return text.replace(/[\u00a0\u202f\u2007\u2009\u200a\ufeff]/g, ' ').replace(/\s+/g, ' ');
}

export function measureText(text: string, fontSize: number, bold = false): number {
  let width = 0;
  for (const char of pdfSafeText(text)) {
    const code = toWinAnsiCode(char);
    const units = HELVETICA_WIDTHS[code] ?? 600;
    width += units * (bold ? 1.05 : 1);
  }
  return (width * fontSize) / 1000;
}

export function wrapText(text: string, maxWidth: number, fontSize: number, bold = false): string[] {
  const value = pdfSafeText(text).trim() || '—';
  if (measureText(value, fontSize, bold) <= maxWidth) return [value];
  const words = value.split(/\s+/);
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (measureText(next, fontSize, bold) <= maxWidth) {
      current = next;
      continue;
    }
    if (current) lines.push(current);
    if (measureText(word, fontSize, bold) <= maxWidth) {
      current = word;
    } else {
      let chunk = '';
      for (const char of word) {
        const attempt = chunk + char;
        if (measureText(attempt, fontSize, bold) <= maxWidth) chunk = attempt;
        else {
          if (chunk) lines.push(chunk);
          chunk = char;
        }
      }
      current = chunk;
    }
  }
  if (current) lines.push(current);
  return lines.length > 0 ? lines : ['—'];
}

function encodeWinAnsi(text: string): string {
  let out = '';
  for (const char of text) {
    const code = toWinAnsiCode(char);
    if (code === 92) out += '\\\\';
    else if (code === 40) out += '\\(';
    else if (code === 41) out += '\\)';
    else if (code >= 32 && code <= 126) out += char;
    else out += `\\${code.toString(8).padStart(3, '0')}`;
  }
  return out;
}

function rgb(color: Rgb): string {
  return `${color[0].toFixed(3)} ${color[1].toFixed(3)} ${color[2].toFixed(3)}`;
}

function roundedPath(x: number, y: number, w: number, h: number, r: number): string {
  const radius = Math.min(r, w / 2, h / 2);
  const k = 0.5522847498 * radius;
  const x1 = x + radius;
  const x2 = x + w - radius;
  const y1 = y + radius;
  const y2 = y + h - radius;
  const right = x + w;
  const top = y + h;
  return [
    `${x1.toFixed(2)} ${y.toFixed(2)} m`,
    `${x2.toFixed(2)} ${y.toFixed(2)} l`,
    `${(x2 + k).toFixed(2)} ${y.toFixed(2)} ${right.toFixed(2)} ${(y1 - k).toFixed(2)} ${right.toFixed(2)} ${y1.toFixed(2)} c`,
    `${right.toFixed(2)} ${y2.toFixed(2)} l`,
    `${right.toFixed(2)} ${(y2 + k).toFixed(2)} ${(x2 + k).toFixed(2)} ${top.toFixed(2)} ${x2.toFixed(2)} ${top.toFixed(2)} c`,
    `${x1.toFixed(2)} ${top.toFixed(2)} l`,
    `${(x1 - k).toFixed(2)} ${top.toFixed(2)} ${x.toFixed(2)} ${(y2 + k).toFixed(2)} ${x.toFixed(2)} ${y2.toFixed(2)} c`,
    `${x.toFixed(2)} ${y1.toFixed(2)} l`,
    `${x.toFixed(2)} ${(y1 - k).toFixed(2)} ${(x1 - k).toFixed(2)} ${y.toFixed(2)} ${x1.toFixed(2)} ${y.toFixed(2)} c`,
  ].join(' ');
}

interface EmbeddedJpeg {
  bytes: Uint8Array;
  width: number;
  height: number;
}

interface PageContent {
  ops: string[];
  imageIndexes: number[];
}

function concatPdfParts(parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.length;
  }
  return out;
}

export class PdfDocument {
  private readonly pages: PageContent[] = [];
  private readonly images: EmbeddedJpeg[] = [];
  y = PAGE_HEIGHT - MARGIN;
  footerLeft = '';
  footerRightPrefix = 'Page';
  footerOf = 'of';

  constructor() {
    this.addPage();
  }

  get pageCount(): number {
    return this.pages.length;
  }

  get contentWidth(): number {
    return CONTENT_WIDTH;
  }

  private get page(): PageContent {
    return this.pages[this.pages.length - 1];
  }

  private addPage() {
    this.pages.push({ ops: [], imageIndexes: [] });
    this.y = PAGE_HEIGHT - MARGIN;
    this.fillRect(0, 0, PAGE_WIDTH, PAGE_HEIGHT, COLORS.page);
    this.fillRect(0, PAGE_HEIGHT - 3.2, PAGE_WIDTH, 3.2, COLORS.accent);
  }

  newPage() {
    this.addPage();
  }

  ensureSpace(height: number) {
    if (this.y - height < MARGIN + FOOTER_H) this.newPage();
  }

  gap(amount: number) {
    this.y -= amount;
  }

  /**
   * Reserve a line box of `leading` points and draw text on a baseline `size`
   * below the current cursor. Advances `y` by the full leading so the next
   * block cannot collide with this line.
   */
  flowLine(
    value: string,
    options: {
      size: number;
      leading: number;
      bold?: boolean;
      color?: Rgb;
      x?: number;
      maxWidth?: number;
      align?: 'left' | 'right' | 'center';
    },
  ) {
    const leading = options.leading;
    this.ensureSpace(leading);
    const baseline = this.y - options.size;
    this.text(value, options.x ?? MARGIN, baseline, {
      size: options.size,
      bold: options.bold,
      color: options.color,
      maxWidth: options.maxWidth,
      align: options.align,
    });
    this.y -= leading;
  }

  fillRect(x: number, y: number, w: number, h: number, color: Rgb) {
    this.page.ops.push(
      `${rgb(color)} rg ${x.toFixed(2)} ${y.toFixed(2)} ${w.toFixed(2)} ${h.toFixed(2)} re f`,
    );
  }

  strokeRect(x: number, y: number, w: number, h: number, color: Rgb, width = 0.6) {
    this.page.ops.push(
      `${rgb(color)} RG ${width} w ${x.toFixed(2)} ${y.toFixed(2)} ${w.toFixed(2)} ${h.toFixed(2)} re S`,
    );
  }

  fillRoundedRect(x: number, y: number, w: number, h: number, r: number, color: Rgb) {
    this.page.ops.push(`${rgb(color)} rg ${roundedPath(x, y, w, h, r)} f`);
  }

  strokeRoundedRect(
    x: number,
    y: number,
    w: number,
    h: number,
    r: number,
    color: Rgb,
    width = 0.6,
  ) {
    this.page.ops.push(`${rgb(color)} RG ${width} w ${roundedPath(x, y, w, h, r)} S`);
  }

  line(x1: number, y1: number, x2: number, y2: number, color: Rgb, width = 0.6) {
    this.page.ops.push(
      `${rgb(color)} RG ${width} w ${x1.toFixed(2)} ${y1.toFixed(2)} m ${x2.toFixed(2)} ${y2.toFixed(2)} l S`,
    );
  }

  /**
   * Draw a JPEG at the current flow cursor, scaled to `maxWidth` while keeping
   * aspect ratio. Returns false when the bytes are not a compatible JPEG.
   */
  drawJpeg(bytes: Uint8Array, options: { x?: number; maxWidth?: number } = {}): boolean {
    const size = parseJpegDimensions(bytes);
    if (!size) return false;
    const maxWidth = options.maxWidth ?? CONTENT_WIDTH;
    const scale = Math.min(1, maxWidth / size.width);
    const displayW = size.width * scale;
    const displayH = size.height * scale;
    if (displayW < 1 || displayH < 1) return false;
    this.ensureSpace(displayH + 8);
    const x = options.x ?? MARGIN;
    const bottom = this.y - displayH;
    const imageIndex = this.images.length;
    this.images.push({ bytes, width: size.width, height: size.height });
    this.page.imageIndexes.push(imageIndex);
    this.page.ops.push(
      `q ${displayW.toFixed(2)} 0 0 ${displayH.toFixed(2)} ${x.toFixed(2)} ${bottom.toFixed(2)} cm /Im${imageIndex + 1} Do Q`,
    );
    this.y = bottom;
    this.gap(8);
    return true;
  }

  text(
    value: string,
    x: number,
    baseline: number,
    options: {
      size: number;
      bold?: boolean;
      color?: Rgb;
      align?: 'left' | 'right' | 'center';
      maxWidth?: number;
    } = {
      size: 9,
    },
  ) {
    const size = options.size;
    const bold = options.bold ?? false;
    const color = options.color ?? COLORS.text;
    let drawX = x;
    const width = measureText(value, size, bold);
    if (options.align === 'right' && options.maxWidth != null) drawX = x + options.maxWidth - width;
    else if (options.align === 'center' && options.maxWidth != null) {
      drawX = x + (options.maxWidth - width) / 2;
    }
    const font = bold ? '/F2' : '/F1';
    this.page.ops.push(
      `BT ${rgb(color)} rg ${font} ${size} Tf 1 0 0 1 ${drawX.toFixed(2)} ${baseline.toFixed(2)} Tm (${encodeWinAnsi(pdfSafeText(value))}) Tj ET`,
    );
  }

  build(): Uint8Array {
    this.pages.forEach((page, index) => {
      const y = 22;
      page.ops.push(
        `${rgb(COLORS.line)} RG 0.5 w ${MARGIN.toFixed(2)} 32 m ${(MARGIN + CONTENT_WIDTH).toFixed(2)} 32 l S`,
      );
      const left = this.footerLeft || 'Clinio';
      const right = `${this.footerRightPrefix} ${index + 1} ${this.footerOf} ${this.pages.length}`;
      page.ops.push(
        `BT ${rgb(COLORS.muted)} rg /F1 7 Tf 1 0 0 1 ${MARGIN.toFixed(2)} ${y.toFixed(2)} Tm (${encodeWinAnsi(pdfSafeText(left))}) Tj ET`,
      );
      const rightX = MARGIN + CONTENT_WIDTH - measureText(right, 7);
      page.ops.push(
        `BT ${rgb(COLORS.muted)} rg /F1 7 Tf 1 0 0 1 ${rightX.toFixed(2)} ${y.toFixed(2)} Tm (${encodeWinAnsi(pdfSafeText(right))}) Tj ET`,
      );
    });

    const encoder = new TextEncoder();
    const objects: Uint8Array[] = [];
    const pushText = (value: string) => {
      objects.push(encoder.encode(`${value}\n`));
    };

    const firstImageId = 3 + this.pages.length * 2;
    const fontRegularId = firstImageId + this.images.length;
    const fontBoldId = fontRegularId + 1;

    pushText('1 0 obj << /Type /Catalog /Pages 2 0 R >> endobj');
    const pageIds = this.pages.map((_, index) => 3 + index * 2);
    pushText(
      `2 0 obj << /Type /Pages /Count ${this.pages.length} /Kids [${pageIds.map((id) => `${id} 0 R`).join(' ')}] >> endobj`,
    );

    let nextId = 3;
    for (const page of this.pages) {
      const content = page.ops.join('\n');
      const contentId = nextId + 1;
      const used = [...new Set(page.imageIndexes)];
      const xObjects =
        used.length > 0
          ? ` /XObject << ${used.map((index) => `/Im${index + 1} ${firstImageId + index} 0 R`).join(' ')} >>`
          : '';
      pushText(
        `${nextId} 0 obj << /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_WIDTH} ${PAGE_HEIGHT}] /Resources << /Font << /F1 ${fontRegularId} 0 R /F2 ${fontBoldId} 0 R >>${xObjects} >> /Contents ${contentId} 0 R >> endobj`,
      );
      pushText(
        `${contentId} 0 obj << /Length ${content.length} >> stream\n${content}\nendstream endobj`,
      );
      nextId += 2;
    }

    for (const [index, image] of this.images.entries()) {
      const header = encoder.encode(
        `${firstImageId + index} 0 obj << /Type /XObject /Subtype /Image /Width ${image.width} /Height ${image.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${image.bytes.length} >> stream\n`,
      );
      const footer = encoder.encode('\nendstream endobj\n');
      objects.push(concatPdfParts([header, image.bytes, footer]));
    }

    pushText(
      `${fontRegularId} 0 obj << /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >> endobj`,
    );
    pushText(
      `${fontBoldId} 0 obj << /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >> endobj`,
    );

    const parts: Uint8Array[] = [encoder.encode('%PDF-1.4\n')];
    const offsets = [0];
    let cursor = parts[0]!.length;
    for (const obj of objects) {
      offsets.push(cursor);
      parts.push(obj);
      cursor += obj.length;
    }
    const xrefAt = cursor;
    let xref = `xref\n0 ${objects.length + 1}\n`;
    xref += '0000000000 65535 f \n';
    for (let i = 1; i < offsets.length; i += 1) {
      xref += `${String(offsets[i]).padStart(10, '0')} 00000 n \n`;
    }
    xref += `trailer << /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefAt}\n%%EOF`;
    parts.push(encoder.encode(xref));
    return concatPdfParts(parts);
  }
}

/** Small Clinio mark. Kept understated so the report title stays dominant. */
export const HEADER_WORDMARK_SIZE = 12;
export const HEADER_WORDMARK_TO_TITLE = 10;
export const HEADER_TITLE_SIZE = 16;
export const HEADER_TITLE_LEADING = 20;
export const HEADER_TITLE_TO_SUBTITLE = 6;
export const HEADER_SUBTITLE_SIZE = 9;
export const HEADER_SUBTITLE_LEADING = 12;
export const HEADER_BLOCK_TO_META = 12;
export const HEADER_META_AFTER = 16;

export interface PdfHeaderMetaItem {
  label: string;
  value: string;
}

export interface PdfReportHeader {
  brand?: string;
  title: string;
  subtitle?: string;
  meta: PdfHeaderMetaItem[];
}

function drawWordmarkBlock(doc: PdfDocument, brand: string) {
  const mark = HEADER_WORDMARK_SIZE;
  doc.ensureSpace(mark);
  const top = doc.y;
  const bottom = top - mark;
  doc.fillRoundedRect(MARGIN, bottom, mark, mark, 3, COLORS.accent);
  doc.text('C', MARGIN, bottom + 2.8, {
    size: 8,
    bold: true,
    color: COLORS.white,
    align: 'center',
    maxWidth: mark,
  });
  doc.text(brand, MARGIN + mark + 6, bottom + 3, { size: 11, bold: true, color: COLORS.text });
  doc.y = bottom;
}

function drawMetaCard(doc: PdfDocument, items: PdfHeaderMetaItem[]) {
  if (items.length === 0) return;
  const colW = CONTENT_WIDTH / items.length;
  const prepared = items.map((item) => ({
    label: item.label,
    lines: wrapText(item.value, colW - 16, 8, true),
  }));
  const metaH = Math.max(34, 16 + Math.max(...prepared.map((item) => item.lines.length)) * 10 + 8);
  doc.ensureSpace(metaH);
  const top = doc.y;
  const bottom = top - metaH;
  doc.fillRoundedRect(MARGIN, bottom, CONTENT_WIDTH, metaH, 6, COLORS.paper);
  doc.strokeRoundedRect(MARGIN, bottom, CONTENT_WIDTH, metaH, 6, COLORS.line, 0.55);
  prepared.forEach((item, index) => {
    const x = MARGIN + 10 + index * colW;
    doc.text(item.label, x, top - 12, { size: 7, color: COLORS.muted, maxWidth: colW - 16 });
    item.lines.forEach((line, lineIndex) => {
      doc.text(line, x, top - 24 - lineIndex * 10, { size: 8, bold: true, maxWidth: colW - 16 });
    });
  });
  doc.y = bottom;
}

/**
 * Stacked finance-report header: brand, title, optional description, then metadata.
 * Each band reserves its own height before the next is drawn so longer titles,
 * wrapping descriptions, or a slightly different logo size cannot overlap.
 */
export function drawReportHeader(doc: PdfDocument, header: PdfReportHeader): void {
  drawWordmarkBlock(doc, header.brand ?? 'Clinio');
  doc.gap(HEADER_WORDMARK_TO_TITLE);

  const titleLines = wrapText(header.title, CONTENT_WIDTH, HEADER_TITLE_SIZE, true);
  for (const line of titleLines) {
    doc.flowLine(line, {
      size: HEADER_TITLE_SIZE,
      leading: HEADER_TITLE_LEADING,
      bold: true,
      color: COLORS.text,
    });
  }

  if (header.subtitle) {
    doc.gap(HEADER_TITLE_TO_SUBTITLE);
    const subtitleLines = wrapText(header.subtitle, CONTENT_WIDTH, HEADER_SUBTITLE_SIZE, false);
    for (const line of subtitleLines) {
      doc.flowLine(line, {
        size: HEADER_SUBTITLE_SIZE,
        leading: HEADER_SUBTITLE_LEADING,
        color: COLORS.muted,
      });
    }
  }

  doc.gap(HEADER_BLOCK_TO_META);
  drawMetaCard(doc, header.meta);
  doc.gap(HEADER_META_AFTER);
}
